import { Controller, UseFormReturn } from 'react-hook-form';
import { FormField } from '../../ui/form';
import FormContent from '../../molecule/FormContent';
import Input from '../../atom/Input';
import RadioGroup from '../../atom/RadioGroup';
import Select from '../../atom/Select';
import SingleImageUpload from '../../atom/SingleImageUpload';
import IconButton from '../../atom/Button/IconButton';
import { Media } from '@/src/models/MeidaDto';
import DatePicker from '../../atom/DatePicker';
import dayjs from 'dayjs';

// TEXT
const FormSectionTextRenderer =
  () =>
  (
    form: UseFormReturn<any, any, any>,
    name: string,
    label: string,
    isRequired?: boolean,
    placeholder?: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormContent label={label} error={error} isRequired={isRequired}>
            <Input
              onClear={() => {
                field.onChange('');
              }}
              type="text"
              size={40}
              placeholder={placeholder}
              {...field}
            />
          </FormContent>
        );
      }}
    />
  );

// RADIO
interface FormSectionRadioRendererProps {
  options: { label: React.ReactNode; value: string }[];
}
const FormSectionRadioRenderer =
  ({ options }: FormSectionRadioRendererProps) =>
  (
    form: UseFormReturn<any, any, any>,
    name: string,
    label: string,
    isRequired?: boolean,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormContent label={label} error={error} isRequired={isRequired}>
            <RadioGroup
              optionList={options.map(item => ({
                ...item,
                className:
                  'items-start [&>button]:relative [&>button]:top-[5px]',
              }))}
              groupProps={{
                className:
                  'items-start [&>button]:relative [&>button]:top-[5px]',
              }}
              rootProps={{
                ...field,
                onValueChange: field.onChange,
              }}
            />
          </FormContent>
        );
      }}
    />
  );

// SELECT_WITH_INPUT
interface FormSectionSelectWithInputRenderProps {
  options: { label: string; value: string }[];
}
const FormSectionSelectWithInputRender =
  ({ options }: FormSectionSelectWithInputRenderProps) =>
  (
    form: UseFormReturn<any, any, any>,
    name: [string, string],
    label: string,
    isRequired?: boolean,
    placeholder?: [string, string],
  ) => {
    const [selectName, inputName] = name;

    return (
      <FormField
        control={form.control}
        name={inputName}
        render={({ field, fieldState: { error } }) => {
          return (
            <Controller
              control={form.control}
              name={selectName}
              render={({
                field: selectField,
                fieldState: { error: selectError },
              }) => {
                const { ref: selectRef, ...selectFieldRest } = selectField;

                return (
                  <FormContent
                    label={label}
                    error={error || selectError}
                    isRequired={isRequired}
                  >
                    <div className="flex gap-2">
                      <Select
                        option={[{ optionList: options }]}
                        rootProps={{
                          ...selectFieldRest,
                          value: selectField.value,
                          onValueChange: selectField.onChange,
                        }}
                        triggerProps={{ style: { width: 136 } }}
                        valueProps={{ placeholder: placeholder?.[0] }}
                        ref={selectRef}
                      />
                      <Input
                        containerClassName="flex-1"
                        {...field}
                        placeholder={placeholder?.[1]}
                        onClear={() => {
                          field.onChange('');
                        }}
                      />
                    </div>
                  </FormContent>
                );
              }}
            />
          );
        }}
      />
    );
  };

// IMAGE_UPLOAD
interface FormSectionImageUploadRenderProps {
  isMultiple: boolean;
  maxLength?: number;
}
const FormSectionImageUploadRender =
  (props?: FormSectionImageUploadRenderProps) =>
  (
    form: UseFormReturn<any, any, any>,
    name: string,
    label: string,
    isRequired?: boolean,
  ) => {
    if (props?.isMultiple) {
      return (
        <FormField
          control={form.control}
          name={name}
          render={({ field, fieldState: { error } }) => {
            return (
              <FormContent
                label={label}
                error={error}
                isRequired={isRequired}
                defaultMessage={`${
                  props?.maxLength ? `최대 ${props?.maxLength}장` : undefined
                }`}
              >
                <div className="flex flex-wrap gap-2">
                  {((field?.value as Media[]) || [])?.map((item, idx) => (
                    <SingleImageUpload
                      key={idx.toString()}
                      {...field}
                      value={
                        field?.value?.[idx]?.bufferFullUrl
                          ? field?.value?.[idx]?.bufferFullUrl
                          : undefined
                      }
                      onChange={media => {
                        field.onChange(
                          ((field?.value as Media[]) || []).map(
                            (item, itemIdx) => (idx === itemIdx ? media : item),
                          ),
                        );
                      }}
                      topAccessary={({ handleEdit, handleRemove }) => {
                        return (
                          <div className="flex gap-x-1">
                            <IconButton
                              buttonSize="xSmall"
                              buttonColor="outline"
                              lucideIconProps={{ name: 'Pencil' }}
                              onClick={() => {
                                handleEdit();
                              }}
                            />
                            <IconButton
                              buttonSize="xSmall"
                              buttonColor="outline"
                              lucideIconProps={{ name: 'X' }}
                              onClick={() => {
                                handleRemove();
                                field.onChange(
                                  ((field?.value as Media[]) || []).filter(
                                    (item, itemIdx) => idx !== itemIdx,
                                  ),
                                );
                              }}
                            />
                          </div>
                        );
                      }}
                    />
                  ))}
                  {((field?.value as Media[]) || []).length <
                    (props?.maxLength || 5) && (
                    <SingleImageUpload
                      {...field}
                      value={undefined}
                      onChange={media => {
                        field.onChange([...(field?.value as Media[]), media]);
                      }}
                    />
                  )}
                </div>
              </FormContent>
            );
          }}
        />
      );
    }

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormContent label={label} error={error} isRequired={isRequired}>
              <SingleImageUpload
                {...field}
                value={
                  field?.value?.bufferFullUrl
                    ? field?.value?.bufferFullUrl
                    : undefined
                }
                onChange={media => {
                  field.onChange(media);
                }}
                topAccessary={({ handleEdit, handleRemove }) => {
                  return (
                    <div className="flex gap-x-1">
                      <IconButton
                        buttonSize="xSmall"
                        buttonColor="outline"
                        lucideIconProps={{ name: 'Pencil' }}
                        onClick={() => {
                          handleEdit();
                        }}
                      />
                      <IconButton
                        buttonSize="xSmall"
                        buttonColor="outline"
                        lucideIconProps={{ name: 'X' }}
                        onClick={() => {
                          handleRemove();
                          field.onChange(null);
                        }}
                      />
                    </div>
                  );
                }}
              />
            </FormContent>
          );
        }}
      />
    );
  };

const FormSectionDateRender =
  () =>
  (
    form: UseFormReturn<any, any, any>,
    name: string,
    label: string,
    isRequired?: boolean,
    placehodler?: string,
  ) => {
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormContent label={label} error={error} isRequired={isRequired}>
              <DatePicker
                {...field}
                placeholder={placehodler}
                onChange={e => {
                  field.onChange(
                    e ? dayjs(e).format('YYYY-MM-DD HH:mm:ss') : '',
                  );
                }}
              />
            </FormContent>
          );
        }}
      />
    );
  };

export const FormSctionRenderer = {
  TEXT: FormSectionTextRenderer,
  RADIO: FormSectionRadioRenderer,
  SELECT_WITH_INPUT: FormSectionSelectWithInputRender,
  IMAGE_UPLOAD: FormSectionImageUploadRender,
  DATE: FormSectionDateRender,
};
